import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { on, reportDepth } from '@/bridge';

/**
 * `goBackOr` from the RN AppBar: pop history, or fall back when there is none.
 *
 * The fallback matters more on the web than it did natively — a deep link or a
 * refresh on /customer/c1 leaves nothing to pop, and `navigate(-1)` would walk
 * the user out of the app entirely.
 */
export function useGoBack(fallback = '/') {
  const navigate = useNavigate();
  const location = useLocation();

  return useCallback(() => {
    const canPop = location.key !== 'default' && window.history.length > 1;
    if (canPop) navigate(-1);
    else navigate(fallback, { replace: true });
  }, [navigate, location.key, fallback]);
}

/**
 * Tracks how many things the hardware back button could close, and keeps the
 * shell informed.
 *
 * Depth counts route stack plus open overlays. A tab root is 0, which is what
 * lets Android exit the app from Home instead of trapping the user.
 */
export function useBackHandling(isTabRoot: boolean, overlaysOpen: number, onOverlayBack?: () => void) {
  const location = useLocation();
  const goBack = useGoBack();
  const overlayRef = useRef(onOverlayBack);
  overlayRef.current = onOverlayBack;

  const depth = (isTabRoot ? 0 : 1) + overlaysOpen;

  useEffect(() => {
    reportDepth(depth);
  }, [depth, location.pathname]);

  useEffect(
    () =>
      on('nav.back', () => {
        // An open sheet swallows back; only then does it leave the screen.
        if (overlaysOpen > 0 && overlayRef.current) overlayRef.current();
        else if (!isTabRoot) goBack();
      }),
    [overlaysOpen, isTabRoot, goBack],
  );
}

/**
 * A viewport height that is correct below Chrome 108, where `dvh` does not exist.
 *
 * Publishes `--k2-vh`. Full-height surfaces should use
 * `height: var(--k2-vh); height: 100dvh` so modern engines take the native unit
 * and older ones fall back to the measured value.
 */
export function useViewportHeight(): void {
  useEffect(() => {
    const apply = () => {
      const h = window.visualViewport?.height ?? window.innerHeight;
      document.documentElement.style.setProperty('--k2-vh', `${h}px`);
    };
    apply();
    window.visualViewport?.addEventListener('resize', apply);
    window.addEventListener('resize', apply);
    window.addEventListener('orientationchange', apply);
    return () => {
      window.visualViewport?.removeEventListener('resize', apply);
      window.removeEventListener('resize', apply);
      window.removeEventListener('orientationchange', apply);
    };
  }, []);
}

/* `useHydrated` lived here, waiting for a persisted store to rehydrate. Nothing
   in the app persists any more — the books are on the server — so no store has
   a `.persist` to wait on and calling it could only have thrown. */
